import React, { useEffect, useState } from "react";
import { useBranch } from '../../../layouts/BranchContext';
import {
  Button,
  TextField,
  Tooltip,
  Container,
  Typography,
  InputAdornment,
  Box,
  Table,
  Modal,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  CircularProgress,
} from "@mui/material";

import InfoOutlineIcon from "@mui/icons-material/InfoOutline";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Close as CloseIcon,
  KeyboardDoubleArrowLeft as ChevronLeft,
  KeyboardDoubleArrowRight as ChevronRight,
} from "@mui/icons-material";

import SearchIcon from "@mui/icons-material/Search";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  getOrganizationBranchesPath,
} from "../../../../utils/branchHelper";

// If you already have this helper, use it instead of local scrollbar CSS.
// Change the import path according to your project.
// import { tableScrollbarStyles } from "../utils/tableScrollbarStyles";

const Alert = React.forwardRef(function Alert(props, ref) {
  return (
    <MuiAlert
      elevation={6}
      ref={ref}
      variant="filled"
      {...props}
    />
  );
});

const Course = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { selectedBranchId, branchList, isMainBranchAdmin } = useBranch();

  // ---------------------------------------------------------
  // Pagination
  // ---------------------------------------------------------
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const rowsPerPageOptions = [5, 10, 25, 50];

  // ---------------------------------------------------------
  // Snackbar
  // ---------------------------------------------------------
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // ---------------------------------------------------------
  // Search
  // ---------------------------------------------------------
  const [searchTerm, setSearchTerm] = useState("");

  // ---------------------------------------------------------
  // Modals
  // ---------------------------------------------------------
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showUserGuideModal, setShowUserGuideModal] =
    useState(false);

  const [selectedCourse, setSelectedCourse] = useState(null);

  // ---------------------------------------------------------
  // Loading
  // ---------------------------------------------------------
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);

  // ---------------------------------------------------------
  // Course data
  // ---------------------------------------------------------
  const [courses, setCourses] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  // ---------------------------------------------------------
  // Organization
  // ---------------------------------------------------------
  const [organizationId, setOrganizationId] = useState(null);


  // ---------------------------------------------------------
  // API Base URL
  // ---------------------------------------------------------
  const API_BASE_URL =
    process.env.REACT_APP_API_BASE_URL ||
    "https://wims.revisetoexcel.com";

  // ---------------------------------------------------------
  // Get stored user
  // ---------------------------------------------------------
  const getStoredUser = () => {
    try {
      const sessionUser = JSON.parse(
        sessionStorage.getItem("user") || "{}"
      );

      const localUser = JSON.parse(
        localStorage.getItem("user") || "{}"
      );

      // Session user has priority
      if (
        sessionUser &&
        Object.keys(sessionUser).length > 0
      ) {
        return sessionUser;
      }

      return localUser || {};
    } catch (error) {
      console.error("Error parsing stored user:", error);
      return {};
    }
  };

  // ---------------------------------------------------------
  // Get organization ID
  // ---------------------------------------------------------
  const getOrganizationId = () => {
    const user = getStoredUser();

    if (user?.organization_id) {
      return user.organization_id;
    }

    try {
      const client = JSON.parse(
        localStorage.getItem("client") || "{}"
      );

      if (client?.organization_id) {
        return client.organization_id;
      }

      if (client?.id) {
        return client.id;
      }
    } catch (error) {
      console.error("Error reading client:", error);
    }

    return null;
  };

  // ---------------------------------------------------------
  // Get authentication token
  // ---------------------------------------------------------
  const getAuthToken = () => {
    return (
      localStorage.getItem("token") ||
      localStorage.getItem("authToken") ||
      localStorage.getItem("access_token") ||
      localStorage.getItem("accessToken") ||
      sessionStorage.getItem("token") ||
      sessionStorage.getItem("authToken") ||
      sessionStorage.getItem("access_token") ||
      sessionStorage.getItem("accessToken")
    );
  };

  // ---------------------------------------------------------
  // Auth headers
  // ---------------------------------------------------------
  const getAuthHeaders = () => {
    const token = getAuthToken();

    const headers = {
      "Content-Type": "application/json",
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  };

  // =========================================================
  // INITIAL ORGANIZATION
  // =========================================================
  useEffect(() => {
    const orgId = getOrganizationId();

    if (orgId) {
      setOrganizationId(orgId);
    } else {
      console.warn(
        "Organization ID not found from logged-in user."
      );
    }
  }, []);

 
  // =========================================================
  // FETCH COURSES
  // =========================================================
  const loadAllData = async (branchId = selectedBranchId) => {
  try {
    setLoading(true);
    const token = getAuthToken();
    if (!token) {
      setSnackbarMessage("Authentication required. Please log in.");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
      setLoading(false);
      return;
    }

    // Build query parameters
    const params = {};
    if (branchId) {
      params.branch = branchId;
    }

    const response = await axios.get(`${API_BASE_URL}/api/course/courses/`, {
      params,
      headers: getAuthHeaders(),
    });

    // Handle API response formats
    const responseData = response?.data;
    let coursesArray = [];
    if (Array.isArray(responseData)) {
      coursesArray = responseData;
    } else if (Array.isArray(responseData?.results)) {
      coursesArray = responseData.results;
    } else if (Array.isArray(responseData?.data)) {
      coursesArray = responseData.data;
    }

    setCourses(coursesArray);
    setFilteredData(coursesArray);
    setPage(1);
  } catch (error) {
    console.error("Error fetching courses:", error);
    if (error?.response?.status === 401) {
      setSnackbarMessage("Session expired. Please log in again.");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
      localStorage.removeItem("token");
      localStorage.removeItem("authToken");
      localStorage.removeItem("access_token");
      localStorage.removeItem("accessToken");
      sessionStorage.removeItem("token");
      sessionStorage.removeItem("authToken");
      sessionStorage.removeItem("access_token");
      sessionStorage.removeItem("accessToken");
      return;
    }
    setSnackbarMessage(error?.response?.data?.message || "Failed to load courses. Please try again.");
    setSnackbarSeverity("error");
    setOpenSnackbar(true);
    setCourses([]);
    setFilteredData([]);
  } finally {
    setLoading(false);
  }
};

  // =========================================================
  // LOAD COURSES WHEN BRANCH CHANGES
  // =========================================================
  useEffect(() => {
  loadAllData(selectedBranchId);
}, [selectedBranchId]);

  // =========================================================
  // SEARCH
  // =========================================================
  useEffect(() => {
    const search = searchTerm
      .trim()
      .toLowerCase();

    if (!search) {
      setFilteredData(courses);
      setPage(1);
      return;
    }

    const filtered = courses.filter(
      (course) => {
        const courseCode =
          String(course?.course_code || "")
            .toLowerCase();

        const courseName =
          String(course?.course_name || "")
            .toLowerCase();

        const courseDuration =
          String(course?.course_duration || "")
            .toLowerCase();

        const courseDescription =
          String(
            course?.course_description || ""
          ).toLowerCase();

        const courseFee =
          String(
            course?.total_course_fee || ""
          ).toLowerCase();

        return (
          courseCode.includes(search) ||
          courseName.includes(search) ||
          courseDuration.includes(search) ||
          courseDescription.includes(search) ||
          courseFee.includes(search)
        );
      }
    );

    setFilteredData(filtered);
    setPage(1);
  }, [searchTerm, courses]);

  // =========================================================
  // EDIT
  // =========================================================
  const handleEdit = (course) => {
    navigate(`/edit-course/${course.id}`);
  };

  // =========================================================
  // ADD
  // =========================================================
  const handleAddCourse = () => {
    navigate("/add-course");
  };

  // =========================================================
  // DELETE MODAL
  // =========================================================
  const handleShowDeleteModal = (course) => {
    setSelectedCourse(course);
    setShowDeleteModal(true);
  };

  // =========================================================
  // DELETE
  // =========================================================
  const handleDelete = async () => {
    if (!selectedCourse) {
      return;
    }

    try {
      setDeleting(true);

      const token = getAuthToken();

      if (!token) {
        setSnackbarMessage(
          "Authentication required. Please log in."
        );
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
        setDeleting(false);
        setShowDeleteModal(false);
        return;
      }

      await axios.delete(
        `${API_BASE_URL}/api/course/courses/${selectedCourse.id}/`,
        {
          headers: getAuthHeaders(),
        }
      );

      // Remove deleted course immediately
      setCourses((prev) =>
        prev.filter(
          (course) =>
            course.id !== selectedCourse.id
        )
      );

      setFilteredData((prev) =>
        prev.filter(
          (course) =>
            course.id !== selectedCourse.id
        )
      );

      setSnackbarMessage(
        `${
          selectedCourse.course_name ||
          "Course"
        } deleted successfully!`
      );

      setSnackbarSeverity("success");
      setOpenSnackbar(true);

      setShowDeleteModal(false);
      setSelectedCourse(null);
    } catch (error) {
      console.error(
        "Error deleting course:",
        error
      );

      if (
        error?.response?.status === 401
      ) {
        setSnackbarMessage(
          "Session expired. Please log in again."
        );
        setSnackbarSeverity("error");
      } else {
        setSnackbarMessage(
          error?.response?.data?.message ||
            "Failed to delete course. Please try again."
        );
        setSnackbarSeverity("error");
      }

      setOpenSnackbar(true);
    } finally {
      setDeleting(false);
    }
  };

  // =========================================================
  // CLOSE DELETE MODAL
  // =========================================================
  const handleCloseDeleteModal = () => {
    if (deleting) {
      return;
    }

    setShowDeleteModal(false);
    setSelectedCourse(null);
  };

  // =========================================================
  // PAGINATION
  // =========================================================
  const handleChangePage = (
    event,
    newPage
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event
  ) => {
    setRowsPerPage(
      parseInt(event.target.value, 10)
    );
    setPage(1);
  };

  const totalPages = Math.max(
    1,
    Math.ceil(
      filteredData.length / rowsPerPage
    )
  );

  const paginatedData =
    filteredData.slice(
      (page - 1) * rowsPerPage,
      page * rowsPerPage
    );

  // =========================================================
  // RENDER
  // =========================================================
  return (
    <Container maxWidth={false}>
      {/* =====================================================
          TOP TOOLBAR
      ====================================================== */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          mb: 1,
          p: 2,
          width: "100%",
          boxSizing: "border-box",
          flexWrap: "wrap",
        }}
      >
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          {/* SEARCH */}
          <TextField
            variant="outlined"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) =>
              setSearchTerm(e.target.value)
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    sx={{
                      color:
                        theme.palette.primary.main,
                      ml: 1.5,
                      fontSize: 24,
                    }}
                  />
                </InputAdornment>
              ),
              endAdornment:
                searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() =>
                        setSearchTerm("")
                      }
                      sx={{
                        color:
                          theme.palette.text
                            .secondary,
                        mr: 1,
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              sx: {
                borderRadius: "30px",
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.15)"
                    : "#fff",

                boxShadow:
                  theme.palette.mode === "dark"
                    ? "0 2px 8px rgba(0,0,0,0.3)"
                    : "0 2px 8px rgba(0,0,0,0.1)",

                "& .MuiOutlinedInput-notchedOutline":
                  {
                    border:
                      theme.palette.mode ===
                      "dark"
                        ? "1px solid rgba(255,255,255,0.2)"
                        : "1px solid rgba(0,0,0,0.1)",
                  },

                "&:hover": {
                  "& .MuiOutlinedInput-notchedOutline":
                    {
                      borderColor:
                        theme.palette.primary.main,
                    },
                },

                "&.Mui-focused": {
                  "& .MuiOutlinedInput-notchedOutline":
                    {
                      border: `2px solid ${theme.palette.primary.main}`,
                    },

                  boxShadow: `0 0 0 3px ${theme.palette.primary.main}20`,
                },

                height: "50px",
                transition: "all 0.3s ease",
              },
            }}
            sx={{
              width: 350,
              maxWidth: "100%",

              "& .MuiInputBase-input": {
                padding: "10px 14px",
                color:
                  theme.palette.text.primary,
                fontWeight: 500,
              },
            }}
          />

          {/* ADD COURSE */}
          <Button
            variant="contained"
            onClick={handleAddCourse}
            startIcon={<AddIcon />}
            sx={{
              ml: 1,
              width: 160,
              height: 50,
              borderRadius: "10px",
              fontWeight: "bold",
              backgroundColor:
                theme.palette.primary.main,

              "&:hover": {
                backgroundColor:
                  theme.palette.primary.dark,
              },
            }}
          >
            ADD COURSE
          </Button>
        </Box>

        {/* USER GUIDE */}
        <IconButton
          onClick={() =>
            setShowUserGuideModal(true)
          }
          sx={{
            width: 60,
            height: 60,
            borderRadius: "50%",
            color:
              theme.palette.primary.main,
          }}
        >
          <InfoOutlineIcon
            sx={{ fontSize: 30 }}
          />
        </IconButton>
      </Box>

      {/* =====================================================
          TABLE
      ====================================================== */}
      <Paper
        elevation={3}
        sx={{
          width: "100%",
          maxWidth: "100%",
          overflow: "hidden",
          backgroundColor:
            theme.palette.background.paper,
          borderRadius: "16px",
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 300,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer
            sx={{
              width: "100%",
              maxHeight:
                "calc(100vh - 300px)",

              overflow: "auto",

              "&::-webkit-scrollbar": {
                width: 8,
                height: 8,
              },

              "&::-webkit-scrollbar-thumb": {
                backgroundColor:
                  theme.palette.primary.main,
                borderRadius: 10,
              },

              "&::-webkit-scrollbar-track": {
                backgroundColor:
                  "transparent",
              },

              scrollbarWidth: "thin",

              scrollbarColor: `${theme.palette.primary.main} transparent`,
            }}
          >
            <Table
              stickyHeader
              sx={{
                minWidth: 1000,

                "& .MuiTableHead-root .MuiTableCell-root":
                  {
                    fontWeight: "bold",
                    backgroundColor:
                      theme.palette.primary.main,
                    color: "#fff",
                  },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      minWidth: 70,
                      whiteSpace: "nowrap",
                      fontSize: "1.1rem",
                    }}
                  >
                    S.No
                  </TableCell>
                  <TableCell
                    sx={{
                      minWidth: 150,
                      whiteSpace: "nowrap",
                      fontSize: "1.1rem",
                    }}
                  >
                    Course Code
                  </TableCell>

                  <TableCell
                    sx={{
                      minWidth: 200,
                      whiteSpace: "nowrap",
                      fontSize: "1.1rem",
                    }}
                  >
                    Course Name
                  </TableCell>

                  <TableCell
                    sx={{
                      minWidth: 150,
                      whiteSpace: "nowrap",
                      fontSize: "1.1rem",
                    }}
                  >
                    Course Duration
                  </TableCell>

                  <TableCell
                    sx={{
                      minWidth: 250,
                      whiteSpace: "nowrap",
                      fontSize: "1.1rem",
                    }}
                  >
                    Course Description
                  </TableCell>

                  <TableCell
                    sx={{
                      minWidth: 180,
                      whiteSpace: "nowrap",
                      fontSize: "1.1rem",
                    }}
                  >
                    Course Fee
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      minWidth: 120,
                      whiteSpace: "nowrap",
                      fontSize: "1.1rem",
                    }}
                  >
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {paginatedData.length > 0 ? (
                  paginatedData.map(
                    (course, index) => (
                      <TableRow
                        key={course.id}
                        hover
                      >
                        <TableCell>
                          {(page - 1) * rowsPerPage + index + 1}
                        </TableCell>
                        <TableCell
                          sx={{
                            whiteSpace:
                              "nowrap",
                          }}
                        >
                          {course.course_code ||
                            "N/A"}
                        </TableCell>

                        <TableCell
                          sx={{
                            whiteSpace:
                              "nowrap",
                          }}
                        >
                          {course.course_name ||
                            "N/A"}
                        </TableCell>

                        <TableCell
                          sx={{
                            whiteSpace:
                              "nowrap",
                          }}
                        >
                          {course.course_duration ||
                            "N/A"}
                        </TableCell>
<TableCell>
  <Box
    sx={{
      maxWidth: 250,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    }}
    title={course.course_description || "N/A"}
  >
    {course.course_description || "N/A"}
  </Box>
</TableCell>

                        <TableCell
                          sx={{
                            whiteSpace:
                              "nowrap",
                          }}
                        >
                          ₹
                          {course.total_course_fee ||
                            "0"}
                        </TableCell>

                        <TableCell align="center">
                          <Tooltip title="Edit">
                            <IconButton
                              onClick={() =>
                                handleEdit(
                                  course
                                )
                              }
                              sx={{
                                color:
                                  theme.palette
                                    .primary.main,

                                "&:hover": {
                                  backgroundColor:
                                    theme.palette
                                      .action.hover,
                                },
                              }}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Delete">
                            <IconButton
                              color="error"
                              onClick={() =>
                                handleShowDeleteModal(
                                  course
                                )
                              }
                              sx={{
                                "&:hover": {
                                  backgroundColor:
                                    theme.palette
                                      .action.hover,
                                },
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    )
                  )
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      align="center"
                      sx={{
                        py: 5,
                        fontSize: "1rem",
                      }}
                    >
                      No Data Found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* =====================================================
          PAGINATION
      ====================================================== */}
      {filteredData.length > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent:
              "space-between",
            alignItems: "center",
            mt: 1,
            p: 2,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color:
                theme.palette.primary.main,
            }}
          >
            Page <strong>{page}</strong> of{" "}
            <strong>{totalPages}</strong>
          </Typography>

          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
          >
            {/* PREVIOUS */}
            <IconButton
              onClick={() =>
                handleChangePage(
                  null,
                  page - 1
                )
              }
              disabled={page === 1}
              sx={{
                color:
                  theme.palette.primary.main,
                width: 36,
                height: 36,
              }}
            >
              <ChevronLeft />
            </IconButton>

            {/* PAGE NUMBERS */}
            {Array.from(
              { length: totalPages },
              (_, index) => index + 1
            ).map((pageNumber) => (
              <IconButton
                key={pageNumber}
                onClick={() =>
                  handleChangePage(
                    null,
                    pageNumber
                  )
                }
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",

                  backgroundColor:
                    page === pageNumber
                      ? theme.palette.primary
                          .main
                      : "transparent",

                  color:
                    page === pageNumber
                      ? theme.palette.primary
                          .contrastText
                      : theme.palette.text
                          .primary,

                  "&:hover": {
                    backgroundColor:
                      page === pageNumber
                        ? theme.palette.primary
                            .dark
                        : theme.palette.action
                            .hover,
                  },
                }}
              >
                {pageNumber}
              </IconButton>
            ))}

            {/* NEXT */}
            <IconButton
              onClick={() =>
                handleChangePage(
                  null,
                  page + 1
                )
              }
              disabled={page >= totalPages}
              sx={{
                color:
                  theme.palette.primary.main,
                width: 36,
                height: 36,
              }}
            >
              <ChevronRight />
            </IconButton>

            {/* ROWS */}
            <FormControl
              size="small"
              sx={{
                minWidth: 100,
                ml: 1,

                "& .MuiInputLabel-root": {
                  color:
                    theme.palette.text
                      .secondary,

                  "&.Mui-focused": {
                    color:
                      theme.palette.primary
                        .main,
                  },
                },
              }}
            >
              <InputLabel id="rows-label">
                Rows
              </InputLabel>

              <Select
                labelId="rows-label"
                value={rowsPerPage}
                onChange={
                  handleChangeRowsPerPage
                }
                label="Rows"
                sx={{
                  height: 36,
                  color:
                    theme.palette.text
                      .primary,
                }}
              >
                {rowsPerPageOptions.map(
                  (option) => (
                    <MenuItem
                      key={option}
                      value={option}
                    >
                      {option}
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>
          </Stack>
        </Box>
      )}

      {/* =====================================================
          SNACKBAR
      ====================================================== */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() =>
          setOpenSnackbar(false)
        }
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Alert
          onClose={() =>
            setOpenSnackbar(false)
          }
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* =====================================================
          DELETE MODAL
      ====================================================== */}
      <Modal
        open={showDeleteModal}
        onClose={handleCloseDeleteModal}
        BackdropProps={{
          style: {
            backgroundColor:
              "rgba(0,0,0,0.8)",
          },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform:
              "translate(-50%, -50%)",
            width: 400,
            maxWidth: "90%",
            bgcolor:
              "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              backgroundColor:
                theme.palette.primary.main,
              p: 1.5,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: "#fff",
              }}
            >
              Delete Confirmation ⚠️
            </Typography>
          </Box>

          <Box sx={{ p: 2.5 }}>
            <Typography sx={{ mb: 2 }}>
              Are you sure you want to
              delete{" "}
              <strong>
                {
                  selectedCourse?.course_name
                }
              </strong>
              ?
            </Typography>

            <Box
              sx={{
                display: "flex",
                justifyContent:
                  "flex-end",
                mt: 3,
              }}
            >
              <Button
                onClick={
                  handleCloseDeleteModal
                }
                variant="outlined"
                sx={{ mr: 1 }}
                disabled={deleting}
               data-app-cancel="true">
                Cancel
              </Button>

              <Button
                variant="contained"
                color="error"
                onClick={handleDelete}
                disabled={deleting}
                startIcon={
                  deleting ? (
                    <CircularProgress
                      size={20}
                      color="inherit"
                    />
                  ) : null
                }
              >
                {deleting
                  ? "Deleting..."
                  : "Yes, Delete"}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* =====================================================
          USER GUIDE MODAL
      ====================================================== */}
      <Modal
        open={showUserGuideModal}
        onClose={() =>
          setShowUserGuideModal(false)
        }
        BackdropProps={{
          style: {
            backgroundColor:
              "rgba(0,0,0,0.8)",
          },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform:
              "translate(-50%, -50%)",
            width: 800,
            maxWidth: "95%",
            bgcolor:
              "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              backgroundColor:
                theme.palette.primary.main,
              p: 1.5,
              display: "flex",
              justifyContent:
                "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              variant="h5"
              sx={{
                color: "#fff",
                fontWeight: "bold",
              }}
            >
              Course Management - User Guide
            </Typography>

            <IconButton
              onClick={() =>
                setShowUserGuideModal(
                  false
                )
              }
              sx={{
                color: "#fff",
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box
            sx={{
              maxHeight: "70vh",
              overflowY: "auto",
              p: 2.5,

              "&::-webkit-scrollbar": {
                width: 8,
              },

              "&::-webkit-scrollbar-thumb": {
                backgroundColor:
                  theme.palette.primary.main,
                borderRadius: 10,
              },

              "&::-webkit-scrollbar-track": {
                backgroundColor:
                  "transparent",
              },
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontWeight: "bold",
                color:
                  theme.palette.primary.main,
                mb: 1,
              }}
            >
              ➤ Follow these steps to add a
              new course:
            </Typography>

            <ul
              style={{
                marginLeft: "20px",
                lineHeight: "1.8",
              }}
            >
              <li>
                Click the{" "}
                <strong>
                  ADD COURSE
                </strong>{" "}
                button to open the add
                course form.
              </li>
              <li>
                Enter the{" "}
                <strong>
                  Course Code
                </strong>
                ,{" "}
                <strong>
                  Course Name
                </strong>{" "}
                and{" "}
                <strong>
                  Course Duration
                </strong>
                .
              </li>
              <li>
                Provide a course
                description.
              </li>
              <li>
                Enter the course fee.
              </li>
              <li>
                Review the information.
              </li>
              <li>
                Click{" "}
                <strong>SAVE</strong>.
              </li>
            </ul>

            <Typography
              variant="body1"
              sx={{
                fontWeight: "bold",
                color:
                  theme.palette.primary.main,
                mt: 3,
                mb: 1,
              }}
            >
              ➤ Edit an existing course:
            </Typography>

            <ul
              style={{
                marginLeft: "20px",
                lineHeight: "1.8",
              }}
            >
              <li>
                Select the course from
                the table.
              </li>
              <li>
                Click the{" "}
                <strong>
                  Edit
                </strong>{" "}
                icon.
              </li>
              <li>
                Update the required
                fields.
              </li>
              <li>
                Click{" "}
                <strong>
                  UPDATE
                </strong>
                .
              </li>
            </ul>

            <Typography
              variant="body1"
              sx={{
                fontWeight: "bold",
                color:
                  theme.palette.primary.main,
                mt: 3,
                mb: 1,
              }}
            >
              ➤ Delete a course:
            </Typography>

            <ul
              style={{
                marginLeft: "20px",
                lineHeight: "1.8",
              }}
            >
              <li>
                Select the course.
              </li>
              <li>
                Click the{" "}
                <strong>
                  Delete
                </strong>{" "}
                icon.
              </li>
              <li>
                Confirm using{" "}
                <strong>
                  Yes, Delete
                </strong>
                .
              </li>
            </ul>

            <Typography
              variant="body2"
              sx={{
                mt: 2,
                color:
                  theme.palette.text.primary,
              }}
            >
              ⚠️ Note: Deleted courses
              cannot be recovered.
            </Typography>

            <Typography
              variant="body1"
              sx={{
                fontWeight: "bold",
                color:
                  theme.palette.primary.main,
                mt: 3,
                mb: 1,
              }}
            >
              ➤ Search for a course:
            </Typography>

            <ul
              style={{
                marginLeft: "20px",
                lineHeight: "1.8",
              }}
            >
              <li>
                Use the search bar.
              </li>
              <li>
                Search by course code,
                name, duration,
                description or fee.
              </li>
            </ul>

            <Typography
              variant="body1"
              sx={{
                fontWeight: "bold",
                color:
                  theme.palette.primary.main,
                mt: 3,
                mb: 1,
              }}
            >
              ➤ Branch filtering:
            </Typography>

            <ul
              style={{
                marginLeft: "20px",
                lineHeight: "1.8",
              }}
            >
              <li>
                Select{" "}
                <strong>
                  All Branches
                </strong>{" "}
                to display courses from
                all branches.
              </li>
              <li>
                Select a specific branch
                to display courses belonging
                to that branch.
              </li>
              <li>
                The course list reloads
                automatically when the
                branch changes.
              </li>
            </ul>

            <Typography
              variant="body1"
              sx={{
                fontWeight: "bold",
                color:
                  theme.palette.primary.main,
                mt: 3,
                mb: 1,
              }}
            >
              ➤ Navigate the course list:
            </Typography>

            <ul
              style={{
                marginLeft: "20px",
                lineHeight: "1.8",
              }}
            >
              <li>
                Use the pagination controls.
              </li>
              <li>
                Use the left/right arrows.
              </li>
              <li>
                Select a page number.
              </li>
              <li>
                Change rows per page.
              </li>
            </ul>
          </Box>
        </Box>
      </Modal>
    </Container>
  );
};

export default Course;